import {
  EditGreenCard,
  PossessionBaseEnum,
  RequestGreenCard,
  VehicleType,
  ZoneEnum
} from '@insurance/api/requests'
import { deviceSession, formatDateToISOFormat } from '@shared/utils'
import { NamePath } from 'antd/es/form/interface'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'

import { GreenCardFormValues } from './types'

export const GREEN_CARD_FORM_DATA_KEY = '__green_card_insurance_form_data__'

export const saveGreenCardValuesToStorage = (
  values: GreenCardFormValues,
  id: string | number = ''
) => {
  sessionStorage.setItem(id + GREEN_CARD_FORM_DATA_KEY, JSON.stringify(values))
}

export const getGreenCardValuesFromStorage = (id: string | number = '') => {
  const values = sessionStorage.getItem(id + GREEN_CARD_FORM_DATA_KEY)

  if (!values) return

  const parsedValues = JSON.parse(values)
  const { validFromProps, possessionBaseDocDateProps } = formProps()

  return {
    ...parsedValues,
    ...(parsedValues[validFromProps.name] && {
      [validFromProps.name]: dayjs(parsedValues[validFromProps.name])
    }),
    ...(parsedValues[possessionBaseDocDateProps.name] && {
      [possessionBaseDocDateProps.name]: dayjs(
        parsedValues[possessionBaseDocDateProps.name]
      )
    })
  }
}

export const removeGreenCardValuesFromStorage = (id: string | number = '') =>
  sessionStorage.removeItem(id + GREEN_CARD_FORM_DATA_KEY)

export const zoneLabelMap: Partial<Record<ZoneEnum, string>> = {
  [ZoneEnum.Z3]: 'europe',
  [ZoneEnum.Z1]: 'ukraine'
}

export const formProps = (tFunction?: TFunction) => {
  const t = tFunction ?? (() => '')

  return {
    vehicleTypeProps: { name: 'vehicle_type', label: t('vehicle_type') },
    contractorTypeProps: {
      name: 'contractor_type',
      label: t('person_type')
    },
    identificationNumberProps: {
      name: 'identification_number',
      label: `${t('identification_number')} / ${t('fiscal_code')}`
    },
    certificateNumberProps: {
      name: 'certificate_number',
      label: t('certificate_number')
    },
    trailerCertificateNumberProps: {
      name: 'trailer_certificate_number',
      label: t('trailer_registration_certificate')
    },
    zoneProps: { name: 'zone', label: t('coverage_zone') },
    termProps: { name: 'term', label: t('insurance_period') },
    validFromProps: { name: 'valid_from', label: t('valid_from') },
    possessionBaseProps: {
      name: 'possession_base',
      label: t('possession_base')
    },
    possessionBaseDocNumberProps: {
      name: 'possession_base_doc_number',
      label: t('document_number')
    },
    possessionBaseDocDateProps: {
      name: 'possession_base_doc_date',
      label: t('document_date')
    },
    possessionBaseDocOtherTitleProps: {
      name: 'possession_base_doc_other_title',
      label: t('document_title')
    },
    possessionBaseDocNoteProps: {
      name: 'possession_base_doc_note',
      label: t('document_note')
    },
    vehicleDataProps: { name: 'vehicle_data' },
    contractorPersonProps: { name: 'contractor_person' },
    vehicleSelectedProps: { name: 'vehicle_selected' },
    trailerVehicleSelectedProps: { name: 'trailer_vehicle_selected' },
    customerProps: { name: 'customer', label: t('customer') },
    creditProps: {
      name: 'credit',
      label: t('amount_used_from_balance')
    },

    // Hidden
    stepKey: { name: 'step' },
    priceKey: { name: 'price' },
    orderKey: { name: 'order_id' },
    isPropertyKey: { name: 'is_property' },
    warningsKey: { name: 'underwriting_warnings' }
  } satisfies Record<string, { name: NamePath; label?: React.ReactNode }>
}

export const normalizeGreenCardData = (
  values: GreenCardFormValues
): RequestGreenCard => {
  const session = deviceSession()

  return {
    session: {
      device_id: session.deviceId,
      session_id: session.sessionId
    },
    identification_number: values.identification_number,
    certificate_number: values.certificate_number,
    ...(values.vehicle_type === VehicleType.TRAILER &&
      values.trailer_certificate_number && {
        trailer_certificate_number: values.trailer_certificate_number
      }),
    zone: values.zone,
    term: values.term
  } as RequestGreenCard
}

export const normalizeOrderData = (values: GreenCardFormValues) => {
  const greenCardData = normalizeGreenCardData(values)

  return {
    ...greenCardData,
    valid_from: formatDateToISOFormat(values.valid_from)!,
    ...(values.possession_base && {
      possession_base: values.possession_base,
      ...(values.possession_base !== PossessionBaseEnum.PROPERTY && {
        ...(values.possession_base_doc_number && {
          possession_base_doc_number: values.possession_base_doc_number
        }),
        ...(values.possession_base_doc_date && {
          possession_base_doc_date: formatDateToISOFormat(
            values.possession_base_doc_date
          )
        }),
        ...(values.possession_base_doc_other_title && {
          possession_base_doc_other_title:
            values.possession_base_doc_other_title
        }),
        ...(values.possession_base_doc_note && {
          possession_base_doc_note: values.possession_base_doc_note
        })
      })
    }),
    customer: values.customer
  } as EditGreenCard
}
